/* Settings → "Shake test" (every role): shows what this phone's motion sensor reports, and counts shakes with exactly the
   same rule as Shake to undo (the same shakeDetector), so the owner can see on his own iPhone why a shake is or is not
   recognised. Nothing is saved or sent anywhere; it stops when Stop is tapped or the app is closed. */
import {shakeDetector} from '../lib/shakeDetect.ts';
import {askMotion, setShakeEnabled, shakeEnabled, type MotionAnswer} from './shake.ts';

interface Run { answer: MotionAnswer | 'asking'; times: number[]; kind: string; now: number; peaks: {t: number; v: number}[]; count: number; startedAt: number }
let run: Run | null = null, timer = 0, flashUntil = 0;
let onMotion: ((ev: Event) => void) | null = null;

const ANSWER: Record<Run['answer'], string> = {
  asking: 'Asking…', granted: 'Allowed', denied: 'Refused — see below', none: 'This device has no motion sensor',
  'not-a-tap': 'The phone did not ask — tap “Start shake test” again'};
const fmt = (v: number) => v.toFixed(1);

export function renderShakeTest(): string {
  return `<div class="card" id="shakeSwitchCard">
    <h2>Shake to undo</h2>
    <label class="h-switch" style="display:inline-flex; align-items:center; gap:10px; font-weight:600; cursor:pointer;">
      <input type="checkbox" class="switch" id="shakeEnabled" ${shakeEnabled() ? 'checked' : ''}><span id="shakeEnabledLabel">${shakeEnabled() ? 'On' : 'Off'}</span></label>
    <p class="muted-text" style="margin-top:8px;">Off: the app never asks for motion access, and deleted entries come back with the Undo bar.
      On: after a swipe-delete you can shake within 30 seconds to undo. On iPhone, also turn off Settings → Accessibility → Touch → Shake to Undo,
      or the iPhone's own 'Undo Typing' box appears too.</p>
  </div>
  <div class="card" id="shakeTestCard">
    <h2>Shake test</h2>
    <p class="muted-text">Checks whether this phone lets the tracker feel a shake (used by “shake to undo” after a swipe-delete).
      Tap Start, allow motion if the phone asks, then shake the phone the way you would to undo. Nothing is saved or sent anywhere.</p>
    <div class="row"><button class="btn primary" id="shakeStartBtn" ${run ? 'hidden' : ''}>Start shake test</button>
      <button class="btn" id="shakeStopBtn" ${run ? '' : 'hidden'}>Stop</button></div>
    <div id="shakeLive" style="margin-top:10px;">${liveHtml()}</div>
  </div>`;
}

function liveHtml(): string {
  if (!run) return '';
  const t = performance.now(), perSec = run.times.filter(x => t - x <= 1000).length;
  const strongest = Math.max(0, ...run.peaks.filter(p => t - p.t <= 2000).map(p => p.v));
  const noData = run.answer !== 'asking' && run.answer !== 'none' && !run.times.length && t - run.startedAt > 2000;
  const refused = run.answer === 'denied';
  return `<p><strong>Motion permission:</strong> <span id="shakeAnswer">${ANSWER[run.answer]}</span></p>
    <p><strong>Readings arriving:</strong> <span id="shakeRate">${run.times.length ? `yes, ${perSec} per second` : 'none yet'}</span></p>
    <p><strong>Data the phone gives:</strong> <span id="shakeKind">${run.kind}</span></p>
    <div class="row" style="align-items:baseline; gap:18px; margin:6px 0;">
      <div><div class="muted-text">Strength now (m/s²)</div><div id="shakeNow" style="font-size:40px; font-weight:700; line-height:1.1;">${fmt(run.now)}</div></div>
      <div><div class="muted-text">Strongest in last 2 s</div><div id="shakeMax" style="font-size:24px; font-weight:700;">${fmt(strongest)}</div></div>
    </div>
    <p id="shakeCountLine" style="font-size:18px; padding:6px 8px; border-radius:8px; ${performance.now() < flashUntil ? 'background:var(--accent); color:#fff;' : ''}">
      <strong>Shakes recognised: <span id="shakeCount">${run.count}</span></strong></p>
    <p class="muted-text">A shake counts when the phone is pushed one way and back (3 pushes, each above 10 m/s², within 1 second).
      Walking or picking the phone up stays below 10.</p>
    ${noData ? `<p class="muted-text" style="color:var(--warn)">No readings are arriving. This device may have no motion sensor, or motion access is off (see below).</p>` : ''}
    ${refused || noData ? `<p class="muted-text"><strong>To turn motion access back on (iPhone):</strong> iPhone asks once and then remembers the answer for a while.
      First close the tracker completely (swipe it away in the app switcher), open it again and tap “Start shake test” — it usually asks again.
      If it still does not ask: for the Home Screen app, remove it from the Home Screen and add it again from Safari (Share → Add to Home Screen; you will need to sign in again);
      in Safari itself, go to iPhone Settings → Apps → Safari → Advanced → Website Data, and remove time.octobermarketing.com (older iPhones: Settings → Safari → Advanced → Website Data).
      On very old iPhones (iOS 12) there is a switch: Settings → Safari → Motion &amp; Orientation Access.</p>` : ''}`;
}

function paint() {
  const el = document.getElementById('shakeLive');
  if (!el) { stop(); return; }                               // left Settings: stop listening
  el.innerHTML = liveHtml();
}

function start() {
  if (run) return;
  run = {answer: 'asking', times: [], kind: 'none yet', now: 0, peaks: [], count: 0, startedAt: performance.now()};
  const r = run;
  askMotion().then(a => { r.answer = a; paint(); });         // called straight inside the tap (iPhone requires that)
  const feed = shakeDetector(undefined, v => { r.now = v; r.peaks.push({t: performance.now(), v}); });
  const vec = (a: DeviceMotionEventAcceleration | null | undefined) => a && a.x != null && a.y != null ? {x: a.x, y: a.y, z: a.z ?? 0} : null;
  onMotion = ev => {
    const e = ev as DeviceMotionEvent, linear = vec(e.acceleration), withGravity = vec(e.accelerationIncludingGravity), t = performance.now();
    r.kind = linear ? 'acceleration without gravity (best)' : withGravity ? 'acceleration with gravity only (gravity is taken out here)' : 'none (empty readings)';
    r.times.push(t); r.times = r.times.filter(x => t - x <= 1000);
    r.peaks = r.peaks.filter(p => t - p.t <= 2000);
    if (feed({linear, withGravity}, t)) {
      r.count++; flashUntil = t + 600;
      try { navigator.vibrate?.(150); } catch { /* not on iPhone */ }
    }
  };
  addEventListener('devicemotion', onMotion);
  timer = window.setInterval(paint, 100);
  swapButtons();
}
function stop() {
  if (onMotion) removeEventListener('devicemotion', onMotion);
  onMotion = null; clearInterval(timer); run = null;
  const el = document.getElementById('shakeLive'); if (el) el.innerHTML = '';
  swapButtons();
}
function swapButtons() {
  document.getElementById('shakeStartBtn')?.toggleAttribute('hidden', !!run);
  document.getElementById('shakeStopBtn')?.toggleAttribute('hidden', !run);
}

export function wireShakeTest() {
  const s = document.getElementById('shakeStartBtn'), x = document.getElementById('shakeStopBtn');
  if (s) s.onclick = start;
  if (x) x.onclick = stop;
  const sw = document.getElementById('shakeEnabled') as HTMLInputElement | null;
  if (sw) sw.onchange = () => {                               // straight inside the tap: switching on may ask on iPhone
    void setShakeEnabled(sw.checked);
    const l = document.getElementById('shakeEnabledLabel'); if (l) l.textContent = sw.checked ? 'On' : 'Off';
  };
}
